import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { can, useAuth, ROLE } from '../lib/auth';

interface CuttingQueueRow {
  jobId: string;
  paperStockId: number;
  netIssued: number;
  jobStatus: string;
  plannedSheets: number;
  productName: string | null;
  clientName: string | null;
  boardSpec: string;
  sheetsCut: number;
  cutSize: string;
  ups: string;
  piecesPerSheet: number | null;
}

interface RequestRow {
  id: number;
  jobId: string;
  paperStockId: number;
  requestedSheets: number;
  reason: string;
  status: string;
  requestedOn: string;
  requestedBy: string;
  resolvedOn: string | null;
  resolvedBy: string | null;
  boardSpec: string;
  currentStock: number;
}

/** The cutting reports start here (the API holds the same date). */
const REPORT_START_DAY = '2026-10-27';
const notBefore = (d: string) => (d < REPORT_START_DAY ? REPORT_START_DAY : d);

export function CuttingQueuePage() {
  const [tab, setTab] = useState<'queue' | 'history' | 'requests' | 'new'>('queue');
  const { user } = useAuth();
  // Operators (and VPPL / Planner) record cuts and ask for sheets; GM and PC
  // only look, so they get no buttons and see everyone's requests.
  const canRecord = can(user, 'RECORD_CUTTING');
  // The Consumption and Daily Sheet reports live under Inventory > Boards >
  // Reports; the operator's own hours are still entered from here.
  const [shiftStep, setShiftStep] = useState<{ step: 'start' | 'end'; shiftId?: number } | null>(null);
  const [shiftSaved, setShiftSaved] = useState('');
  const openShifts = useOpenShifts(canRecord);
  const onShift = openShifts.data?.rows ?? [];
  // The counts on the tabs share the tabs' own queries.
  const queue = useQuery({
    queryKey: ['cutting-queue'],
    queryFn: () => api<{ rows: CuttingQueueRow[] }>('/api/cutting/queue').then(r => r.rows),
  });
  const requests = useQuery({
    queryKey: ['cutting-requests', canRecord ? 'mine' : 'all'],
    queryFn: () => api<{ rows: RequestRow[] }>(`/api/cutting/requests${canRecord ? '?mine=1' : ''}`).then(r => r.rows),
  });
  const pendingRequests = (requests.data ?? []).filter((r) => r.status === 'PENDING').length;
  const queueJobs = new Set((queue.data ?? []).map((r) => r.jobId)).size;

  const TabBtn = ({ k, label, count, tone = 'slate' }: { k: typeof tab; label: string; count?: number; tone?: 'slate' | 'amber' }) => (
    <button
      onClick={() => setTab(k)}
      className={`relative -mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-1 pb-3 pt-1 text-sm font-semibold transition-colors ${
        tab === k ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-800'
      }`}
    >
      {label}
      {count != null && count > 0 && (
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
          tone === 'amber' ? 'bg-amber-100 text-amber-800' : tab === k ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-600'
        }`}>{count.toLocaleString('en-IN')}</span>
      )}
    </button>
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-4 md:px-6 pt-4 shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Cutting</h1>
            <p className="text-xs text-slate-500">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
          </div>
          {canRecord && (
            <div className="flex items-center gap-2">
              {shiftSaved && <span className="hidden sm:inline text-xs font-medium text-emerald-700">✓ {shiftSaved}</span>}
              <button
                onClick={() => { setShiftSaved(''); setShiftStep({ step: 'start' }); }}
                className="inline-flex items-center gap-1.5 whitespace-nowrap px-3.5 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg shadow-sm hover:bg-emerald-700"
              >
                <PlayIcon /> Start shift
              </button>
              <button
                onClick={() => { setShiftSaved(''); setShiftStep({ step: 'end' }); }}
                className="inline-flex items-center gap-1.5 whitespace-nowrap px-3.5 py-2 border border-slate-300 bg-white text-slate-700 text-sm font-semibold rounded-lg shadow-sm hover:bg-slate-50"
              >
                <StopIcon className="text-red-600" /> End shift
              </button>
            </div>
          )}
        </div>

        {/* Who is working now */}
        {canRecord && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">On shift</span>
            {onShift.length === 0 ? (
              <span className="text-slate-500">
                Nobody yet ·{' '}
                <button className="font-semibold text-emerald-700 hover:underline" onClick={() => setShiftStep({ step: 'start' })}>start a shift</button>
              </span>
            ) : onShift.map((s) => {
              // A shift from an earlier day was never ended: shown in amber, with its day.
              const old = s.date !== workDayNow();
              return (
                <span key={s.shiftId} title={old ? `Started ${openSince(s)} and never ended — end it with its real end time` : undefined}
                  className={`inline-flex items-center gap-2 rounded-full border py-1 pl-2.5 pr-1 ${old ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}>
                  <span className={`h-2 w-2 rounded-full ${old ? 'bg-amber-500' : 'bg-emerald-500 animate-pulse'}`} aria-hidden />
                  <span className="font-semibold">{s.operator}</span>
                  <span className={`text-xs ${old ? 'text-amber-800' : 'text-emerald-700'}`}>
                    {SHIFT_TEXT[s.shift] ?? s.shift} · since {openSince(s)}{old && <b> · not ended</b>}
                  </span>
                  <button
                    onClick={() => { setShiftSaved(''); setShiftStep({ step: 'end', shiftId: s.shiftId }); }}
                    className={`rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 hover:text-red-700 border ${old ? 'border-amber-300' : 'border-emerald-200'}`}
                    title={`End ${s.operator}'s shift`}
                  >End</button>
                </span>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-x-6">
          <TabBtn k="queue" label="To cut" count={queueJobs} />
          <TabBtn k="history" label="Cut history" />
          <TabBtn k="requests" label={canRecord ? 'My requests' : 'Requests'} count={pendingRequests} tone="amber" />
          {canRecord && <TabBtn k="new" label="Request extra sheets" />}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4 md:p-6">
        {tab === 'queue' ? <QueueTab canRecord={canRecord} />
          : tab === 'history' ? <CutHistoryTab />
          : tab === 'new' ? <RequestSheetsTab onSent={() => setTab('requests')} />
          : <RequestsTab mine={canRecord} />}
      </div>
      {shiftStep?.step === 'start' && (
        <StartShiftModal
          onClose={() => setShiftStep(null)}
          onSaved={(name, at) => { setShiftStep(null); setShiftSaved(`${name}'s shift started at ${at}`); }}
        />
      )}
      {shiftStep?.step === 'end' && (
        <EndShiftModal
          initialShiftId={shiftStep.shiftId}
          onClose={() => setShiftStep(null)}
          onStart={() => setShiftStep({ step: 'start' })}
          onSaved={(name) => { setShiftStep(null); setShiftSaved(`${name}'s shift ended`); }}
        />
      )}
    </div>
  );
}

/** The job card's own number: 81671 from VP/092026/81671. */
const shortJob = (id: string) => id.slice(id.lastIndexOf('/') + 1);

/** A job cut from more than one board: its rows are shown together, tinted. */
const CLUB_ROW = 'bg-amber-50 hover:bg-amber-100/60';
const ClubBadge = ({ n }: { n: number }) => (
  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-200 text-amber-900 whitespace-nowrap">{n} boards</span>
);

/** The cutting operators' names. */
function useOperators() {
  return useQuery({
    queryKey: ['cutting-operators'],
    queryFn: () => api<{ rows: string[] }>('/api/cutting/operators').then(r => r.rows),
    staleTime: 5 * 60_000,
  });
}

// The desk shares one login, so the page remembers who was working last.
const OPERATOR_KEY = 'kpjm.cutting.operator';
function lastOperator(): string {
  try { return localStorage.getItem(OPERATOR_KEY) ?? ''; } catch { return ''; }
}
function rememberOperator(name: string) {
  try { localStorage.setItem(OPERATOR_KEY, name); } catch { /* private window: nothing to remember */ }
}

type QueueShow = 'all' | 'fresh' | 'part';
type QueueSort = 'job' | 'most' | 'least';

function QueueTab({ canRecord }: { canRecord: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['cutting-queue'],
    queryFn: () => api<{ rows: CuttingQueueRow[] }>('/api/cutting/queue').then(r => r.rows),
  });

  const [activeJob, setActiveJob] = useState<CuttingQueueRow | null>(null);
  const [search, setSearch] = useState('');
  const [show, setShow] = useState<QueueShow>('all');
  const [sort, setSort] = useState<QueueSort>('job');
  const searchRef = useRef<HTMLInputElement>(null);

  // "/" jumps to the search box, as on most sites.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName)) { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (isLoading) return <div className="text-slate-500">Loading…</div>;
  if (isError) return <div className="text-red-600">Could not load the cutting queue: {(error as Error).message}</div>;

  const all = data ?? [];
  const left = (r: CuttingQueueRow) => Math.max(0, r.netIssued - r.sheetsCut);
  // Every word typed must be found somewhere in the job number, client, product or board.
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const matched = all.filter((r) => {
    if (show === 'fresh' && r.sheetsCut > 0) return false;
    if (show === 'part' && r.sheetsCut <= 0) return false;
    if (!words.length) return true;
    const hay = `${r.jobId} ${r.clientName ?? ''} ${r.productName ?? ''} ${r.boardSpec}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  // Sorted job by job, so a job's boards stay together.
  const groups: CuttingQueueRow[][] = [];
  const at = new Map<string, number>();
  for (const r of matched) {
    if (!at.has(r.jobId)) { at.set(r.jobId, groups.length); groups.push([]); }
    groups[at.get(r.jobId)!]!.push(r);
  }
  const groupLeft = (g: CuttingQueueRow[]) => g.reduce((s, r) => s + left(r), 0);
  if (sort === 'most') groups.sort((a, b) => groupLeft(b) - groupLeft(a));
  if (sort === 'least') groups.sort((a, b) => groupLeft(a) - groupLeft(b));
  const rows = groups.flat();
  // Boards per job, over the whole queue (a search must not un-club a job).
  const boardsOf = new Map<string, number>();
  for (const r of all) boardsOf.set(r.jobId, (boardsOf.get(r.jobId) ?? 0) + 1);

  const fmt = (n: number) => n.toLocaleString('en-IN');
  const totalLeft = all.reduce((s, r) => s + left(r), 0);
  const fresh = all.filter((r) => r.sheetsCut <= 0).length;
  const part = all.length - fresh;
  const narrowed = words.length > 0 || show !== 'all';

  const chip = (k: QueueShow, label: string, n: number) => (
    <button type="button" onClick={() => setShow(k)}
      className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
        show === k ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-brand-400 hover:text-brand-700'
      }`}>
      {label} <span className={show === k ? 'text-white/80' : 'text-slate-400'}>{fmt(n)}</span>
    </button>
  );
  const progress = (r: CuttingQueueRow) => {
    const pct = r.netIssued > 0 ? Math.min(100, Math.round((r.sheetsCut / r.netIssued) * 100)) : 0;
    return (
      <div className="mt-1 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden" title={`${pct}% cut`}>
        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
      </div>
    );
  };
  const markBtn = (r: CuttingQueueRow, wide = false) => (
    <button
      onClick={() => setActiveJob(r)}
      disabled={!r.paperStockId}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-40 ${wide ? 'w-full' : ''}`}
    >
      <ScissorsIcon /> Mark cut
    </button>
  );

  return (
    <>
      {/* Where the queue stands */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {([
          ['Jobs to cut', fmt(new Set(all.map((r) => r.jobId)).size), 'text-slate-800'],
          ['Boards', fmt(all.length), 'text-slate-800'],
          ['Sheets left', fmt(totalLeft), 'text-brand-700'],
          ['Part cut', fmt(part), 'text-emerald-700'],
        ] as const).map(([label, value, cls]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className={`text-2xl font-bold ${cls}`}>{value}</div>
            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      {/* Find and narrow */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative w-full md:w-96">
          <svg aria-hidden viewBox="0 0 20 20" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 fill-slate-400">
            <path d="M8.5 3a5.5 5.5 0 014.38 8.83l3.65 3.64-1.06 1.06-3.64-3.65A5.5 5.5 0 118.5 3zm0 1.5a4 4 0 100 8 4 4 0 000-8z" />
          </svg>
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setSearch(''); }}
            className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-16 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            placeholder="Search job no, client, product or board"
          />
          {search ? (
            <button type="button" onClick={() => setSearch('')} aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full w-7 h-7 text-lg leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700">&times;</button>
          ) : (
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 rounded border border-slate-300 bg-slate-50 px-1.5 text-[11px] text-slate-400">/</kbd>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {chip('all', 'All', all.length)}
          {chip('fresh', 'Not started', fresh)}
          {chip('part', 'Part cut', part)}
        </div>
        <label className="ml-auto flex items-center gap-2 text-xs font-medium text-slate-500">
          Sort
          <select value={sort} onChange={(e) => setSort(e.target.value as QueueSort)}
            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:border-brand-500 focus:outline-none">
            <option value="job">Job order</option>
            <option value="most">Most sheets left</option>
            <option value="least">Fewest sheets left</option>
          </select>
        </label>
      </div>
      {narrowed && (
        <div className="mb-3 text-xs text-slate-500">
          Showing {fmt(rows.length)} of {fmt(all.length)} boards ·{' '}
          <button className="font-semibold text-brand-700 hover:underline" onClick={() => { setSearch(''); setShow('all'); }}>Show all</button>
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center text-slate-500">
          {narrowed ? 'Nothing on the queue matches.' : 'No board issued and waiting to be cut.'}
        </div>
      ) : (
        <>
          {/* Phone: one card per board */}
          <div className="grid gap-3 md:grid-cols-2 xl:hidden">
            {rows.map((r, i) => {
              const boards = boardsOf.get(r.jobId) ?? 1;
              return (
                <div key={i} className={`rounded-xl border bg-white p-4 shadow-sm ${boards > 1 ? 'border-l-4 border-l-amber-400 border-amber-200' : 'border-slate-200'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-lg font-bold text-slate-900">{shortJob(r.jobId)}{boards > 1 && <ClubBadge n={boards} />}</div>
                      <div className="text-sm font-medium text-slate-800 truncate">{r.clientName}</div>
                      <div className="text-xs text-slate-500 truncate">{r.productName}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xl font-bold text-brand-700">{fmt(left(r))}</div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-500">left</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-slate-600">{r.boardSpec}</div>
                  <div className="mt-2 text-[11px] text-slate-500">{fmt(r.sheetsCut)} of {fmt(r.netIssued)} cut</div>
                  {progress(r)}
                  {canRecord && <div className="mt-3">{markBtn(r, true)}</div>}
                </div>
              );
            })}
          </div>

          {/* Desktop: the table */}
          <div className="hidden xl:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-slate-50 text-xs uppercase tracking-wide text-slate-500 font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3">Job no</th>
                    <th className="px-4 py-3">Client & product</th>
                    <th className="px-4 py-3">Board</th>
                    <th className="px-4 py-3 text-right">Issued</th>
                    <th className="px-4 py-3 text-right min-w-[150px]">Left to cut</th>
                    {canRecord && <th className="px-4 py-3 text-right">{' '}</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r, i) => {
                    const boards = boardsOf.get(r.jobId) ?? 1;
                    // The second board of a job sits under the first, without repeating the job.
                    const again = boards > 1 && i > 0 && rows[i - 1]!.jobId === r.jobId;
                    return (
                    <tr key={i} className={boards > 1 ? `${CLUB_ROW} border-l-4 border-l-amber-400` : 'hover:bg-slate-50'}>
                      <td className="px-4 py-3 whitespace-nowrap align-top" title={r.jobId}>
                        {again ? <span className="text-slate-400">↳</span> : <><span className="text-base font-bold text-slate-900">{shortJob(r.jobId)}</span>{boards > 1 && <ClubBadge n={boards} />}</>}
                      </td>
                      <td className="px-4 py-3 min-w-[240px] align-top">
                        {!again && <>
                          <div className="font-semibold text-slate-900">{r.clientName}</div>
                          <div className="text-xs text-slate-500">{r.productName}</div>
                        </>}
                      </td>
                      <td className="px-4 py-3 text-slate-600 min-w-[200px] align-top">{r.boardSpec}</td>
                      <td className="px-4 py-3 text-right text-slate-700 whitespace-nowrap align-top">{fmt(r.netIssued)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap align-top">
                        <div className="text-base font-bold text-brand-700">{fmt(left(r))}</div>
                        <div className="text-[11px] text-slate-500">{r.sheetsCut > 0 ? `${fmt(r.sheetsCut)} cut` : 'not started'}</div>
                        {progress(r)}
                      </td>
                      {canRecord && <td className="px-4 py-3 text-right whitespace-nowrap align-top">{markBtn(r)}</td>}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeJob && (
        <MarkCutModal
          row={activeJob}
          onClose={() => setActiveJob(null)}
          onSuccess={() => { setActiveJob(null); qc.invalidateQueries({ queryKey: ['cutting-queue'] }); }}
        />
      )}
    </>
  );
}

const ScissorsIcon = () => (
  <svg aria-hidden viewBox="0 0 20 20" className="h-4 w-4 fill-none stroke-current" strokeWidth={1.8} strokeLinecap="round">
    <circle cx="5" cy="5" r="2.5" /><circle cx="5" cy="15" r="2.5" /><path d="M7 6.5L17 15M7 13.5L17 5" />
  </svg>
);

function MarkCutModal({ row, onClose, onSuccess }: { row: CuttingQueueRow; onClose: () => void; onSuccess: () => void }) {
  const remaining = Math.max(0, row.netIssued - row.sheetsCut);
  const [sheets, setSheets] = useState<number | null>(remaining || null);
  const [remarks, setRemarks] = useState('');
  const operators = useOperators();
  const [operator, setOperator] = useState(lastOperator());
  // Cut sheets follow the full sheets (x cuts per sheet) until the operator types their own.
  const [cutTyped, setCutTyped] = useState<number | null | undefined>(undefined);
  const autoCut = sheets && row.piecesPerSheet ? sheets * row.piecesPerSheet : null;
  const cutSheets = cutTyped !== undefined ? cutTyped : autoCut;

  const mut = useMutation({
    mutationFn: () => api('/api/cutting/mark-cut', {
      method: 'POST',
      body: JSON.stringify({ jobId: row.jobId, paperStockId: row.paperStockId, sheetsCut: sheets || 0, cutSheets: cutSheets || null, operatorName: operator, remarks }),
    }),
    onSuccess: () => { rememberOperator(operator); onSuccess(); },
  });

  // Esc closes, as the × does.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !mut.isPending) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, mut.isPending]);

  const names = operators.data ?? [];
  const allNames = operator && !names.includes(operator) ? [operator, ...names] : names;
  const over = sheets != null && sheets > remaining;
  const blocked = !operator ? 'Pick who is cutting' : !sheets || sheets <= 0 ? 'Enter the full sheets cut' : null;
  const fmt = (n: number) => n.toLocaleString('en-IN');

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!blocked && !mut.isPending) mut.mutate();
  }

  const inputCls = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';
  const quick = (label: string, value: number) => (
    <button type="button" onClick={() => { setSheets(value); setCutTyped(undefined); }}
      className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
        sheets === value ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-brand-400 hover:text-brand-700'
      }`}>
      {label}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-3 sm:p-4 z-50">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[94vh] flex flex-col overflow-hidden">
        {/* Header: which job and board */}
        <div className="px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-brand-700 to-brand-600 text-white">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wider text-white/70">Mark sheets cut</div>
              <div className="mt-0.5 text-2xl font-bold leading-tight">Job {shortJob(row.jobId)}</div>
              {row.clientName && <div className="mt-1 text-sm font-medium text-white/90 truncate" title={row.clientName}>{row.clientName}</div>}
              {row.productName && <div className="text-xs text-white/75 truncate" title={row.productName}>{row.productName}</div>}
            </div>
            <button type="button" onClick={onClose} aria-label="Close"
              className="shrink-0 rounded-full w-9 h-9 flex items-center justify-center text-2xl leading-none text-white/80 hover:bg-white/15 hover:text-white">&times;</button>
          </div>
          <div className="mt-3 inline-flex max-w-full items-center rounded-md bg-white/15 px-2.5 py-1 text-xs font-medium">
            <span className="truncate" title={row.boardSpec}>{row.boardSpec}</span>
          </div>
        </div>

        <div className="overflow-y-auto overflow-x-hidden px-5 sm:px-6 py-5 space-y-5">
          {/* Where this board stands */}
          <div className="grid grid-cols-3 gap-2 text-center">
            {([['Issued', row.netIssued, 'text-slate-800'], ['Already cut', row.sheetsCut, 'text-slate-500'], ['Left to cut', remaining, 'text-brand-700']] as const).map(([label, n, cls]) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-slate-50 px-2 py-2.5">
                <div className={`text-lg font-bold ${cls}`}>{fmt(n)}</div>
                <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
              </div>
            ))}
          </div>

          {/* 1. Operator */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              <span className="mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700">1</span>
              Who is cutting?
            </label>
            {allNames.length > 0 && allNames.length <= 8 ? (
              <div className="flex flex-wrap gap-2">
                {allNames.map((n) => (
                  <button key={n} type="button" onClick={() => setOperator(n)}
                    className={`rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors ${
                      operator === n ? 'border-brand-600 bg-brand-600 text-white shadow-sm' : 'border-slate-300 bg-white text-slate-700 hover:border-brand-400 hover:bg-brand-50'
                    }`}>
                    {operator === n && '✓ '}{n}
                  </button>
                ))}
              </div>
            ) : (
              <select value={operator} onChange={(e) => setOperator(e.target.value)} className={inputCls}>
                <option value="">{operators.isLoading ? 'Loading…' : 'Pick the operator'}</option>
                {allNames.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            )}
          </div>

          {/* 2. Full sheets */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="cut-full">
              <span className="mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700">2</span>
              Full sheets cut
            </label>
            <input id="cut-full" type="number" inputMode="numeric" min="1" autoFocus
              value={sheets ?? ''} onFocus={(e) => e.target.select()}
              onChange={(e) => setSheets(e.target.value ? Number(e.target.value) : null)}
              className={`${inputCls} text-lg font-semibold ${over ? 'border-amber-400' : ''}`} />
            {remaining > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {quick(`All left · ${fmt(remaining)}`, remaining)}
                {remaining >= 2 && quick(`Half · ${fmt(Math.ceil(remaining / 2))}`, Math.ceil(remaining / 2))}
              </div>
            )}
            {over && (
              <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                That is more than the {fmt(remaining)} sheets left to cut on this board.
              </div>
            )}
          </div>

          {/* 3. Cut pieces */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="cut-pieces">
              <span className="mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700">3</span>
              Cut sheets (qty)
              {cutTyped === undefined && autoCut != null && (
                <span className="ml-2 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">auto</span>
              )}
            </label>
            <input id="cut-pieces" type="number" inputMode="numeric" min="1"
              value={cutSheets ?? ''} onFocus={(e) => e.target.select()}
              onChange={(e) => setCutTyped(e.target.value ? Number(e.target.value) : null)}
              className={inputCls} />
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
              {sheets && row.piecesPerSheet
                ? <span>{fmt(sheets)} full × {row.piecesPerSheet} per sheet = <b className="text-slate-700">{fmt(sheets * row.piecesPerSheet)}</b></span>
                : null}
              {row.cutSize ? <span>· Cut size {row.cutSize} in</span> : <span>No cut size on the job card</span>}
              {row.ups ? <span>· {row.ups} ups</span> : null}
              {cutTyped !== undefined && autoCut != null && (
                <button type="button" className="font-semibold text-brand-700 hover:underline" onClick={() => setCutTyped(undefined)}>Reset to {fmt(autoCut)}</button>
              )}
            </div>
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2" htmlFor="cut-remarks">
              Remarks <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input id="cut-remarks" type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)}
              className={inputCls} placeholder="e.g. board edge damaged" />
          </div>

          {mut.isError && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{cuttingError(mut.error)}</div>}
        </div>

        <div className="px-5 sm:px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500">{blocked ?? (operator ? `By ${operator}` : '')}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-200/60 hover:text-slate-900">Cancel</button>
            <button type="submit" disabled={!!blocked || mut.isPending}
              className="rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {mut.isPending ? 'Saving…' : sheets ? `Save · ${fmt(sheets)} sheets` : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

/** The caller's own requests (mine), or everyone's for a role that only looks. */
interface CutHistoryRow {
  id: number; at: string; day: string; jobCard: string; jobId: string;
  clientName: string; jobName: string; board: string;
  sheetsCut: number; cutSheets: number | null; operator: string; enteredBy: string; remarks: string;
}

/** When each job card was cut and who cut it - every Mark cut entry, newest first. */
function CutHistoryTab() {
  const istToday = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(() => notBefore(new Date(Date.now() + (330 - 6 * 1440) * 60000).toISOString().slice(0, 10)));
  const [to, setTo] = useState(istToday);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  const datesOk = !!from && !!to && from <= to;
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['cutting-history', q, q ? '' : from, q ? '' : to],
    enabled: !!q || datesOk,
    queryFn: () => {
      const sp = new URLSearchParams(q ? { q } : { from, to });
      return api<{ rows: CutHistoryRow[] }>(`/api/cutting/history?${sp}`).then((r) => r.rows);
    },
  });
  const rows = data ?? [];
  const total = rows.reduce((s, r) => s + r.sheetsCut, 0);
  const when = (at: string) => {
    const d = new Date(`${at.replace(' ', 'T')}:00`);
    return Number.isNaN(d.getTime()) ? at
      : `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <label className="label">Search</label>
          <input className="input" placeholder="Job no, client or product" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div>
          <label className="label">From</label>
          <input type="date" className="input" value={from} min={REPORT_START_DAY} disabled={!!q} onChange={(e) => setFrom(notBefore(e.target.value))} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input" value={to} disabled={!!q} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>
      <p className="text-xs text-slate-500">
        {q ? 'Searching every date.' : !datesOk ? 'Pick a From date on or before the To date.' : 'Clear the search to go by dates.'}
        {rows.length > 0 && <> · {rows.length} entr{rows.length === 1 ? 'y' : 'ies'}, {total.toLocaleString('en-IN')} sheets cut</>}
      </p>
      {isLoading && <div className="text-slate-500">Loading...</div>}
      {isError && <div className="text-red-600">Could not load the cut history: {(error as Error).message}</div>}
      {data && rows.length === 0 && <div className="text-slate-500 text-sm">No cuts {q ? `match "${q}"` : 'in these dates'}.</div>}
      {rows.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Cut on</th>
                  <th className="px-4 py-3">Job No</th>
                  <th className="px-4 py-3">Client &amp; product</th>
                  <th className="px-4 py-3">Board</th>
                  <th className="px-4 py-3 text-right">Sheets cut</th>
                  <th className="px-4 py-3">Cut by</th>
                  <th className="px-4 py-3">Entered by</th>
                  <th className="px-4 py-3">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/50 align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-slate-700">{when(r.at)}</td>
                    <td className="px-4 py-3 font-semibold">{r.jobCard}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{r.clientName || '—'}</div>
                      <div className="text-xs text-slate-500">{r.jobName}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{r.board || '—'}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="font-semibold">{r.sheetsCut.toLocaleString('en-IN')}</div>
                      {r.cutSheets != null && <div className="text-[11px] text-slate-500">{r.cutSheets.toLocaleString('en-IN')} cut sheets</div>}
                    </td>
                    <td className="px-4 py-3">{r.operator || <span className="text-slate-400">not given</span>}</td>
                    <td className="px-4 py-3 text-slate-600">{r.enteredBy || '—'}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{r.remarks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RequestsTab({ mine }: { mine: boolean }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['cutting-requests', mine ? 'mine' : 'all'],
    queryFn: () => api<{ rows: RequestRow[] }>(`/api/cutting/requests${mine ? '?mine=1' : ''}`).then(r => r.rows),
  });

  if (isLoading) return <div className="text-slate-500">Loading...</div>;
  if (isError) return <div className="text-red-600">Could not load the requests: {(error as Error).message}</div>;

  const rows = data ?? [];

  return (
    <>
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
            <tr>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Job No</th>
              <th className="px-4 py-3">Board Spec</th>
              <th className="px-4 py-3 text-right">Requested</th>
              <th className="px-4 py-3">Reason</th>
              {!mine && <th className="px-4 py-3">Requested by</th>}
              <th className="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50/50">
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{new Date(r.requestedOn).toLocaleDateString()}</td>
                <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap" title={r.jobId}>{shortJob(r.jobId)}</td>
                <td className="px-4 py-3 text-slate-600 min-w-[200px]">{r.boardSpec}</td>
                <td className="px-4 py-3 text-right font-medium text-slate-900 whitespace-nowrap">{r.requestedSheets.toLocaleString()}</td>
                <td className="px-4 py-3 text-slate-600 min-w-[200px]">{r.reason}</td>
                {!mine && <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.requestedBy || '—'}</td>}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    r.status === 'PENDING' ? 'bg-amber-100 text-amber-800' :
                    r.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={mine ? 6 : 7} className="px-4 py-8 text-center text-slate-500">
                  No requests found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
    </>
  );
}

interface JobInfo {
  jobId: string;
  jobStatus: string;
  clientName: string | null;
  productName: string | null;
  plannedSheets: number;
  boardSpec: string;
  boards: { paperStockId: number; boardSpec: string; netIssued: number; sheetsCut: number }[];
}

/** Ask the store for more sheets on a job, by its number. */
function RequestSheetsTab({ onSent }: { onSent: () => void }) {
  const qc = useQueryClient();
  const [jobNo, setJobNo] = useState('');
  const [lookFor, setLookFor] = useState('');
  const [boardId, setBoardId] = useState(0);
  const [sheets, setSheets] = useState<number | null>(null);
  const [reason, setReason] = useState('');

  const info = useQuery({
    queryKey: ['cutting-job-info', lookFor],
    queryFn: () => api<JobInfo>(`/api/cutting/job-info?jobId=${encodeURIComponent(lookFor)}`),
    enabled: !!lookFor,
    retry: false,
  });
  const job = info.data && lookFor === jobNo.trim() ? info.data : null;
  const closed = !!job && ['Closed', 'Cancelled'].includes(job.jobStatus);
  const board = job?.boards.find((b) => b.paperStockId === boardId) ?? (job?.boards.length === 1 ? job.boards[0] : undefined);

  const mut = useMutation({
    mutationFn: () => api('/api/cutting/requests', {
      method: 'POST',
      body: JSON.stringify({ jobId: job!.jobId, paperStockId: board?.paperStockId ?? 0, requestedSheets: sheets || 0, reason }),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cutting-requests'] }); onSent(); },
  });
  const find = () => { setBoardId(0); setLookFor(jobNo.trim()); };
  const input = 'w-full border px-3 py-2 border-slate-200 rounded-md focus:border-brand-500 focus:ring-brand-500 sm:text-sm';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 max-w-xl p-6 space-y-4">
      <h2 className="text-lg font-bold text-slate-800">Request More Sheets</h2>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Job number</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={jobNo}
            onChange={e => setJobNo(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') find(); }}
            onBlur={() => { if (jobNo.trim() && jobNo.trim() !== lookFor) find(); }}
            className={input}
            placeholder="e.g. 81396"
          />
          <button onClick={find} disabled={!jobNo.trim()} className="px-4 py-2 border border-slate-200 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-50 disabled:opacity-50">
            Find
          </button>
        </div>
        {info.isFetching && <div className="text-xs text-slate-500 mt-1">Looking for the job...</div>}
        {info.isError && lookFor === jobNo.trim() && (
          <div className="text-sm text-red-600 mt-1">{cuttingError(info.error)}</div>
        )}
      </div>

      {job && (
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm">
          <div className="font-medium text-slate-900">Job {shortJob(job.jobId)} <span className="text-xs font-normal text-slate-500">· {job.jobStatus}</span></div>
          <div className="text-slate-700">{job.clientName}</div>
          <div className="text-xs text-slate-500">{job.productName}</div>
          <div className="text-xs text-slate-600 mt-2">Board on the card: {job.boardSpec || '—'} · Sheets with wastage: {job.plannedSheets.toLocaleString()}</div>
          {job.boards.length === 0 && <div className="text-xs text-amber-700 mt-1">No board has been issued to this job yet.</div>}
          {job.boards.length === 1 && (
            <div className="text-xs text-slate-600 mt-1">
              Issued: {job.boards[0]!.boardSpec} — {job.boards[0]!.netIssued.toLocaleString()} sheets, {job.boards[0]!.sheetsCut.toLocaleString()} cut
            </div>
          )}
          {closed && <div className="text-xs text-red-600 mt-1">This job is {job.jobStatus.toLowerCase()}; sheets cannot be requested for it.</div>}
        </div>
      )}

      {job && job.boards.length > 1 && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Board</label>
          <select value={boardId} onChange={e => setBoardId(Number(e.target.value))} className={input}>
            <option value={0}>Choose the board…</option>
            {job.boards.map((b) => (
              <option key={b.paperStockId} value={b.paperStockId}>
                {b.boardSpec} — {b.netIssued.toLocaleString()} issued, {b.sheetsCut.toLocaleString()} cut
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Sheets needed</label>
        <input type="number" min="1" value={sheets ?? ''} onChange={e => setSheets(e.target.value ? Number(e.target.value) : null)} className={input} />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
        <input type="text" value={reason} onChange={e => setReason(e.target.value)} className={input} placeholder="Why are more sheets needed?" />
      </div>
      {mut.isError && <div className="text-sm text-red-600">{cuttingError(mut.error)}</div>}
      <div className="flex justify-end">
        <button
          onClick={() => mut.mutate()}
          disabled={!job || closed || (job.boards.length > 1 && !board) || !sheets || sheets <= 0 || !reason.trim() || mut.isPending}
          className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700 disabled:opacity-50"
        >
          {mut.isPending ? 'Sending...' : 'Send Request'}
        </button>
      </div>
    </div>
  );
}

interface ConsumptionRow {
  date: string;
  jobCard: string;
  jobId: string;
  clientName: string;
  jobName: string;
  fullSheets: number;
  cutSheets: number | null;
  boardL: number | null;
  boardW: number | null;
  cutL: number | null;
  cutW: number | null;
  ups: string;
  sku?: string;
  quality: string;
  gsm: number | null;
  weightKg: number | null;
  rate?: number | null;
  value?: number | null;
  wastePct: number | null;
  wasteKg: number | null;
  wasteValue?: number | null;
  /** Why there is no wastage figure. */
  wasteNote?: string | null;
}

/** Save rows as a CSV that Excel opens (UTF-8 with BOM). */
function downloadCsv(table: (string | number | null | undefined)[][], name: string) {
  const cell = (v: string | number | null | undefined) => {
    const t = v == null ? '' : String(v);
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const blob = new Blob(['\uFEFF' + table.map((r) => r.map(cell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** 2026-10-01 as 01.10.26, the way the cutting desk writes it. */
const sheetDate = (d: string) => `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(2, 4)}`;
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** The cutting desk's consumption sheet, from what was marked cut. */
export function ConsumptionTab() {
  const today = new Date();
  const [from, setFrom] = useState(notBefore(isoDay(new Date(today.getFullYear(), today.getMonth(), 1))));
  const [to, setTo] = useState(isoDay(today));
  // By issue: what the store issued to the jobs (kept from 1 Oct).  By
  // cutting: what was marked cut (reported from 27 Oct).
  const [basis, setBasis] = useState<'issue' | 'cut'>('cut');
  const monthStart = isoDay(new Date(today.getFullYear(), today.getMonth(), 1));
  const pickBasis = (v: 'issue' | 'cut') => {
    setBasis(v);
    setFrom((f) => (v === 'cut' ? notBefore(f) : f === notBefore(monthStart) ? monthStart : f));
  };
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['cutting-consumption', from, to, basis],
    queryFn: () => api<{ rows: ConsumptionRow[]; showValue?: boolean }>(`/api/cutting/consumption?from=${from}&to=${to}${basis === 'issue' ? '&basis=issue' : ''}`),
    enabled: !!from && !!to,
  });
  const all = data?.rows ?? [];
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'product' | 'entry'>('product');
  // Every word typed must be found in the row: job card, client, job name, SKU, quality, GSM or date.
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const rows = words.length === 0 ? all : all.filter((r) => {
    const hay = `${r.jobCard} ${r.clientName} ${r.jobName} ${r.sku ?? ''} ${r.quality} ${r.gsm ?? ''} ${sheetDate(r.date)}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  const showValue = !!data?.showValue;
  const totalWasteKg = rows.reduce((n, r) => n + (r.wasteKg ?? 0), 0);
  const totalWasteValue = rows.reduce((n, r) => n + (r.wasteValue ?? 0), 0);
  // Wastage as a share of the board it is worked out on (rows with no wastage figure are left out of both).
  const wasteBaseKg = rows.reduce((n, r) => n + (r.wasteKg != null ? r.weightKg ?? 0 : 0), 0);
  const wastePctAll = wasteBaseKg > 0 ? Math.round((totalWasteKg / wasteBaseKg) * 10000) / 100 : null;
  // Rows whose trim could not be worked out (a board sent back is not one of them).
  const noWaste = rows.filter((r) => r.wastePct == null && r.fullSheets > 0).length;
  const totalKg = rows.reduce((n, r) => n + (r.weightKg ?? 0), 0);
  const totalValue = rows.reduce((n, r) => n + (r.value ?? 0), 0);
  const noValue = rows.filter((r) => r.value == null).length;
  const money = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('en-IN', { maximumFractionDigits: 0 }));
  // Boards per job in these dates: a job cut from two papers is tinted.
  const paper = (r: ConsumptionRow) => r.sku || `${r.boardL}x${r.boardW}|${r.quality}|${r.gsm}`;
  const papersOf = new Map<string, Set<string>>();
  for (const r of all) papersOf.set(r.jobCard, (papersOf.get(r.jobCard) ?? new Set()).add(paper(r)));
  const totalFull = rows.reduce((n, r) => n + r.fullSheets, 0);
  const totalCut = rows.reduce((n, r) => n + (r.cutSheets ?? 0), 0);

  // Product-wise: every entry of a product (for its client) added together.
  const products = (() => {
    const m = new Map<string, {
      product: string; client: string; jobs: Set<string>; skus: Set<string>;
      fullSheets: number; cutSheets: number; weightKg: number; value: number; noValue: number;
      wasteKg: number; wasteBaseKg: number; wasteValue: number;
    }>();
    for (const r of rows) {
      const key = `${r.jobName.trim().toUpperCase()}|${r.clientName.trim().toUpperCase()}`;
      const g = m.get(key) ?? {
        product: r.jobName || '(no product)', client: r.clientName, jobs: new Set<string>(), skus: new Set<string>(),
        fullSheets: 0, cutSheets: 0, weightKg: 0, value: 0, noValue: 0, wasteKg: 0, wasteBaseKg: 0, wasteValue: 0,
      };
      g.jobs.add(r.jobCard);
      if (r.sku) g.skus.add(r.sku);
      g.fullSheets += r.fullSheets;
      g.cutSheets += r.cutSheets ?? 0;
      g.weightKg += r.weightKg ?? 0;
      if (r.value != null) g.value += r.value; else g.noValue += 1;
      if (r.wasteKg != null) { g.wasteKg += r.wasteKg; g.wasteBaseKg += r.weightKg ?? 0; g.wasteValue += r.wasteValue ?? 0; }
      m.set(key, g);
    }
    return [...m.values()]
      .map((g) => ({ ...g, wastePct: g.wasteBaseKg > 0 ? Math.round((g.wasteKg / g.wasteBaseKg) * 10000) / 100 : null }))
      .sort((a, b) => (showValue ? b.value - a.value : 0) || b.weightKg - a.weightKg || a.product.localeCompare(b.product));
  })();
  const r2 = (v: number) => Math.round(v * 100) / 100;

  const exportCsv = () => {
    if (view === 'product') {
      const head = ['Product', 'Client', 'Job cards', 'SKU', 'Sheets', 'Cut pieces', 'Weight (kg)',
        ...(showValue ? ['Value (Rs)', 'Rs per kg'] : []), 'Trim waste %', 'Trim (kg)', ...(showValue ? ['Trim (Rs)'] : [])];
      const lines = products.map((g) => [g.product, g.client, [...g.jobs].join(' '), [...g.skus].join(' '), g.fullSheets, g.cutSheets, r2(g.weightKg),
        ...(showValue ? [r2(g.value), g.weightKg > 0 && g.value > 0 ? r2(g.value / g.weightKg) : null] : []),
        g.wastePct, r2(g.wasteKg), ...(showValue ? [r2(g.wasteValue)] : [])]);
      downloadCsv([head, ...lines], `consumption-${basis === 'issue' ? 'issued' : 'cut'}-by-product-${from}-to-${to}.csv`);
      return;
    }
    const head = ['Dated', 'Job Card', 'CLIENT', 'Job Name', 'SKU', 'Qty. (full Sheet)', 'Cut sheet (qty)', 'Standard Size (Inch)', 'Standard Size (Inch)2',
      'Cut size', 'Cut size 2', 'Ups', 'QUALITY', 'ISSUED GSM', 'Weight (kg)', ...(showValue ? ['Rate (per kg)', 'Value (Rs)'] : []),
      'Wastage %', 'Wastage (kg)', ...(showValue ? ['Wastage (Rs)'] : [])];
    const lines = rows.map((r) => [sheetDate(r.date), r.jobCard, r.clientName, r.jobName, r.sku ?? '', r.fullSheets, r.cutSheets, r.boardL, r.boardW,
      r.cutL, r.cutW, r.ups, r.quality, r.gsm, r.weightKg, ...(showValue ? [r.rate ?? null, r.value ?? null] : []),
      r.wastePct, r.wasteKg, ...(showValue ? [r.wasteValue ?? null] : [])]);
    downloadCsv([head, ...lines], `consumption-${basis === 'issue' ? 'issued' : 'cut'}-${from}-to-${to}.csv`);
  };
  const dateInput = 'border px-3 py-2 border-slate-200 rounded-md focus:border-brand-500 focus:ring-brand-500 sm:text-sm bg-white';
  const num = (v: number | null) => (v == null ? '—' : v.toLocaleString());

  return (
    <>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Consumption</label>
          <div className="flex rounded-md border border-slate-200 bg-white p-0.5">
            {(['issue', 'cut'] as const).map((v) => (
              <button key={v} type="button" onClick={() => pickBasis(v)}
                      title={v === 'issue' ? 'What the store issued to each job (returns taken off)' : 'What the cutting desk marked cut'}
                      className={`rounded px-3 py-1.5 text-sm font-medium ${basis === v ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                {v === 'issue' ? 'By issue' : 'By cutting'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">View</label>
          <div className="flex rounded-md border border-slate-200 bg-white p-0.5">
            {(['product', 'entry'] as const).map((v) => (
              <button key={v} type="button" onClick={() => setView(v)}
                      className={`rounded px-3 py-1.5 text-sm font-medium ${view === v ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                {v === 'product' ? 'By product' : 'By entry'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
          <input type="date" value={from} min={basis === 'cut' ? REPORT_START_DAY : undefined}
                 onChange={e => setFrom(basis === 'cut' ? notBefore(e.target.value) : e.target.value)} className={dateInput} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className={dateInput} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Search</label>
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`${dateInput} w-64`}
            placeholder="Job card, client, job, SKU…"
          />
        </div>
        <span className="text-xs text-slate-500 pb-2">
          {view === 'product' && <>{products.length} product{products.length === 1 ? '' : 's'} · </>}
          {words.length ? `${rows.length} of ${all.length}` : rows.length} rows · {totalFull.toLocaleString('en-IN')} sheets · {totalCut.toLocaleString('en-IN')} cut pieces
          {' · '}{totalKg.toLocaleString('en-IN', { maximumFractionDigits: 0 })} kg
          {showValue && <> · <span className="font-semibold text-slate-800">₹{money(totalValue)}</span>
            {noValue > 0 && <span className="text-amber-700"> ({noValue} row{noValue === 1 ? '' : 's'} with no rate or size)</span>}</>}
          <br />
          Trim waste: <span className="font-semibold text-slate-800">{wastePctAll == null ? '—' : `${wastePctAll}%`}</span>
          {' · '}{totalWasteKg.toLocaleString('en-IN', { maximumFractionDigits: 0 })} kg
          {showValue && <> · <span className="font-semibold text-slate-800">₹{money(totalWasteValue)}</span></>}
          {noWaste > 0 && <span className="text-amber-700" title={rows.filter((r) => r.wastePct == null && r.fullSheets > 0).slice(0, 12).map((r) => `${r.jobCard}: ${r.wasteNote ?? 'sizes missing'}`).join('\n')}>
            {' '}({noWaste} row{noWaste === 1 ? '' : 's'} without a trim figure - sizes missing or wrong; By entry shows which)</span>}
        </span>
        <button
          onClick={exportCsv}
          disabled={rows.length === 0}
          className="ml-auto px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700 disabled:opacity-50"
        >
          Export to Excel
        </button>
      </div>
      {isLoading && <div className="text-slate-500">Loading...</div>}
      {isError && <div className="text-red-600">Could not load the consumption sheet: {(error as Error).message}</div>}
      {!isLoading && !isError && view === 'product' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                <tr>
                  <th className="px-3 py-3">Product</th>
                  <th className="px-3 py-3">Client</th>
                  <th className="px-3 py-3">Job cards</th>
                  <th className="px-3 py-3">SKU</th>
                  <th className="px-3 py-3 text-right" title="Full board sheets">Sheets</th>
                  <th className="px-3 py-3 text-right" title="The pieces those sheets are cut into">Cut pieces</th>
                  <th className="px-3 py-3 text-right">Weight kg</th>
                  {showValue && <th className="px-3 py-3 text-right">Value ₹</th>}
                  {showValue && <th className="px-3 py-3 text-right">₹ / kg</th>}
                  <th className="px-3 py-3 text-right" title="The board trimmed off in cutting">Trim waste %</th>
                  <th className="px-3 py-3 text-right">Trim kg</th>
                  {showValue && <th className="px-3 py-3 text-right">Trim ₹</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {products.map((g) => (
                  <tr key={`${g.product}|${g.client}`} className="hover:bg-slate-50/50 align-top">
                    <td className="px-3 py-2 font-medium text-slate-900">{g.product}</td>
                    <td className="px-3 py-2 text-slate-700">{g.client || '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{[...g.jobs].join(', ')}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700">{[...g.skus].join(', ') || '—'}</td>
                    <td className="px-3 py-2 text-right font-medium text-slate-900">{g.fullSheets.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-slate-900">{g.cutSheets.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{g.weightKg.toLocaleString('en-IN', { maximumFractionDigits: 1 })}</td>
                    {showValue && <td className="px-3 py-2 text-right font-semibold text-slate-900" title={g.noValue ? `${g.noValue} entr${g.noValue === 1 ? 'y' : 'ies'} with no rate or size left out` : undefined}>
                      {g.value > 0 || g.noValue === 0 ? money(g.value) : '—'}{g.noValue > 0 && <span className="text-amber-700">*</span>}
                    </td>}
                    {showValue && <td className="px-3 py-2 text-right text-slate-600">{g.weightKg > 0 && g.value > 0 ? money(g.value / g.weightKg) : '—'}</td>}
                    <td className={`px-3 py-2 text-right ${(g.wastePct ?? 0) >= 10 ? 'font-medium text-red-600' : 'text-slate-600'}`}>{g.wastePct == null ? '—' : `${g.wastePct}%`}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{g.wastePct == null ? '—' : g.wasteKg.toLocaleString('en-IN', { maximumFractionDigits: 1 })}</td>
                    {showValue && <td className="px-3 py-2 text-right text-slate-900">{g.wastePct == null ? '—' : money(g.wasteValue)}</td>}
                  </tr>
                ))}
                {products.length === 0 && (
                  <tr>
                    <td colSpan={showValue ? 12 : 9} className="px-4 py-8 text-center text-slate-500">{words.length ? 'No row matches the search.' : basis === 'issue' ? 'No board was issued to a job in these dates.' : 'Nothing was marked cut in these dates.'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {showValue && products.some((g) => g.noValue > 0) && (
            <div className="border-t border-slate-100 px-3 py-2 text-xs text-amber-700">* Some entries of this product have no board rate or size, and are left out of its value.</div>
          )}
        </div>
      )}
      {!isLoading && !isError && view === 'entry' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                <tr>
                  <th className="px-3 py-3">{basis === 'issue' ? 'First issued' : 'Dated'}</th>
                  <th className="px-3 py-3">Job Card</th>
                  <th className="px-3 py-3">Client</th>
                  <th className="px-3 py-3">Job Name</th>
                  <th className="px-3 py-3">SKU</th>
                  <th className="px-3 py-3 text-right" title="Full board sheets">Sheets</th>
                  <th className="px-3 py-3 text-right" title="The pieces those sheets are cut into">Cut pieces</th>
                  <th className="px-3 py-3 text-right">Board size (in)</th>
                  <th className="px-3 py-3 text-right">Cut size (in)</th>
                  <th className="px-3 py-3 text-right">Ups</th>
                  <th className="px-3 py-3" title="Board type and brand">Board</th>
                  <th className="px-3 py-3 text-right">GSM</th>
                  <th className="px-3 py-3 text-right">Weight kg</th>
                  {showValue && <th className="px-3 py-3 text-right">₹ / kg</th>}
                  {showValue && <th className="px-3 py-3 text-right">Value ₹</th>}
                  <th className="px-3 py-3 text-right" title="The board trimmed off in cutting">Trim waste %</th>
                  <th className="px-3 py-3 text-right">Trim kg</th>
                  {showValue && <th className="px-3 py-3 text-right">Trim ₹</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={i} className={(papersOf.get(r.jobCard)?.size ?? 1) > 1 ? CLUB_ROW : 'hover:bg-slate-50/50'}>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{sheetDate(r.date)}</td>
                    <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap" title={r.jobId}>
                      <div>{r.jobCard}</div>
                      {(papersOf.get(r.jobCard)?.size ?? 1) > 1 && <div className="mt-0.5"><ClubBadge n={papersOf.get(r.jobCard)!.size} /></div>}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{r.clientName}</td>
                    <td className="px-3 py-2 text-slate-700">{r.jobName}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700 whitespace-nowrap">{r.sku || '—'}</td>
                    <td className="px-3 py-2 text-right font-medium text-slate-900">{num(r.fullSheets)}</td>
                    <td className="px-3 py-2 text-right text-slate-900">{num(r.cutSheets)}</td>
                    <td className="px-3 py-2 text-right text-slate-600 whitespace-nowrap">{r.boardL && r.boardW ? `${r.boardL} × ${r.boardW}` : '—'}</td>
                    <td className="px-3 py-2 text-right text-slate-600 whitespace-nowrap">{r.cutL && r.cutW ? `${r.cutL} × ${r.cutW}` : '—'}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{r.ups}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{r.quality}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{num(r.gsm)}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{num(r.weightKg)}</td>
                    {showValue && <td className="px-3 py-2 text-right text-slate-600">{num(r.rate ?? null)}</td>}
                    {showValue && <td className="px-3 py-2 text-right font-medium text-slate-900">{money(r.value)}</td>}
                    <td className={`px-3 py-2 text-right ${(r.wastePct ?? 0) >= 10 ? 'font-medium text-red-600' : 'text-slate-600'}`} title={r.wasteNote ?? undefined}>
                      {r.wastePct == null ? <span className="text-amber-700">— {r.wasteNote}</span> : `${r.wastePct}%`}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-600">{num(r.wasteKg)}</td>
                    {showValue && <td className="px-3 py-2 text-right text-slate-900">{money(r.wasteValue)}</td>}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={showValue ? 18 : 15} className="px-4 py-8 text-center text-slate-500">{words.length ? 'No row matches the search.' : 'Nothing was marked cut in these dates.'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

interface DailyRow {
  date: string;
  operator: string;
  shiftId: number | null;
  startTime: string;
  endTime: string;
  breakMin: number | null;
  hours: number | null;
  shift: string;
  jobs: number;
  sheets: number;
  fullSheets: number;
}

/** 16.5 hours as 16:30. */
const hhmm = (h: number | null) => (h == null ? '—' : `${Math.floor(h)}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`);
const perHour = (n: number, hours: number) => (hours > 0 ? Math.ceil(n / hours).toLocaleString() : '—');
const perJob = (sheets: number, jobs: number) => (jobs > 0 ? Math.ceil(sheets / jobs).toLocaleString() : '—');

/**
 * The cutting department's daily sheet: each operator's shift and what they
 * cut that working day, with the day-wise and operator-wise summaries.
 */
export function DailySheetTab({ canRecord }: { canRecord: boolean }) {
  const qc = useQueryClient();
  const today = new Date();
  const [from, setFrom] = useState(notBefore(isoDay(new Date(today.getFullYear(), today.getMonth(), 1))));
  const [to, setTo] = useState(isoDay(today));
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['cutting-daily', from, to],
    queryFn: () => api<{ rows: DailyRow[]; shiftsReady: boolean }>(`/api/cutting/operator-report?from=${from}&to=${to}`),
    enabled: !!from && !!to,
  });
  const rows = data?.rows ?? [];
  const [editing, setEditing] = useState<Partial<DailyRow> | null>(null);
  const del = useMutation({
    mutationFn: (id: number) => api(`/api/cutting/shifts/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cutting-daily'] }),
  });

  const sum = (list: DailyRow[]) => ({
    jobs: list.reduce((n, r) => n + r.jobs, 0),
    sheets: list.reduce((n, r) => n + r.sheets, 0),
    hours: list.reduce((n, r) => n + (r.hours ?? 0), 0),
  });
  const group = (key: (r: DailyRow) => string) => {
    const m = new Map<string, DailyRow[]>();
    for (const r of rows) m.set(key(r), [...(m.get(key(r)) ?? []), r]);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([k, list]) => ({ key: k, ...sum(list) }));
  };
  const byDay = group((r) => r.date);
  const byOperator = group((r) => r.operator);
  const total = sum(rows);

  const exportCsv = () => {
    const cell = (v: string | number | null) => {
      const t = v == null ? '' : String(v);
      return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    const line = (cells: (string | number | null)[]) => cells.map(cell).join(',');
    const out = [
      line(['Date', 'Operator', 'Start Time', 'End Time', 'Break', 'Hours', 'Shift', 'Jobs', 'Sheets']),
      ...rows.map((r) => line([sheetDate(r.date), r.operator, r.startTime, r.endTime, r.breakMin, r.hours == null ? '' : hhmm(r.hours), r.shift, r.jobs, r.sheets])),
      line(['Total', '', '', '', '', hhmm(total.hours), '', total.jobs, total.sheets]),
      '',
      line(['Dept Summary']),
      line(['Date', 'Jobs', 'Hours', 'Sheets', 'Jobs per hour', 'Sheets per hour', 'Avg sheets per job']),
      ...byDay.map((d) => line([sheetDate(d.key), d.jobs, hhmm(d.hours), d.sheets, perHour(d.jobs, d.hours), perHour(d.sheets, d.hours), perJob(d.sheets, d.jobs)])),
      '',
      line(['Operator Summary']),
      line(['Operator', 'Jobs', 'Sheets', 'Hours', 'Jobs per hour', 'Sheets per hour', 'Avg sheets per job']),
      ...byOperator.map((o) => line([o.key, o.jobs, o.sheets, hhmm(o.hours), perHour(o.jobs, o.hours), perHour(o.sheets, o.hours), perJob(o.sheets, o.jobs)])),
      line(['Total', total.jobs, total.sheets, hhmm(total.hours), perHour(total.jobs, total.hours), perHour(total.sheets, total.hours), perJob(total.sheets, total.jobs)]),
    ];
    const blob = new Blob(['﻿' + out.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `cutting-daily-sheet-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const dateInput = 'border px-3 py-2 border-slate-200 rounded-md focus:border-brand-500 focus:ring-brand-500 sm:text-sm bg-white';
  const th = 'px-3 py-3', td = 'px-3 py-2';
  const card = 'bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden';
  const head = 'bg-slate-50 text-slate-500 font-medium border-b border-slate-200';

  return (
    <>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
          <input type="date" value={from} min={REPORT_START_DAY} onChange={e => setFrom(notBefore(e.target.value))} className={dateInput} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className={dateInput} />
        </div>
        <div className="ml-auto flex gap-2">
          {canRecord && data?.shiftsReady && (
            <button
              onClick={() => setEditing({ date: isoDay(today), operator: lastOperator(), startTime: '08:30', endTime: '20:30', breakMin: 30, shift: 'D' })}
              className="px-4 py-2 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-md hover:bg-slate-50"
            >
              + Shift entry
            </button>
          )}
          <button onClick={exportCsv} disabled={rows.length === 0} className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700 disabled:opacity-50">
            Export to Excel
          </button>
        </div>
      </div>
      {isLoading && <div className="text-slate-500">Loading...</div>}
      {isError && <div className="text-red-600">Could not load the daily sheet: {(error as Error).message}</div>}
      {data && !data.shiftsReady && (
        <div className="mb-4 text-sm text-amber-700">Shift hours cannot be entered yet: the database update for cutting operators has not been run.</div>
      )}
      {data && (
        <div className="space-y-6">
          <div className={card}>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className={head}>
                  <tr>
                    <th className={th}>Date</th>
                    <th className={th}>Operator</th>
                    <th className={th}>Start Time</th>
                    <th className={th}>End Time</th>
                    <th className={`${th} text-right`}>Break</th>
                    <th className={`${th} text-right`}>Hours</th>
                    <th className={th}>Shift</th>
                    <th className={`${th} text-right`}>Jobs</th>
                    <th className={`${th} text-right`}>Sheets</th>
                    {canRecord && <th className={th}></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={`${r.date}|${r.operator}`} className="hover:bg-slate-50/50">
                      <td className={`${td} text-slate-600 whitespace-nowrap`}>{sheetDate(r.date)}</td>
                      <td className={`${td} font-medium text-slate-900`}>{r.operator}</td>
                      <td className={`${td} text-slate-600`}>{r.startTime || '—'}</td>
                      <td className={`${td} text-slate-600`}>{r.endTime || '—'}</td>
                      <td className={`${td} text-right text-slate-600`}>{r.breakMin ?? '—'}</td>
                      <td className={`${td} text-right text-slate-900`}>{hhmm(r.hours)}</td>
                      <td className={`${td} text-slate-600`}>{r.shift || '—'}</td>
                      <td className={`${td} text-right text-slate-900`}>{r.jobs.toLocaleString()}</td>
                      <td className={`${td} text-right font-medium text-slate-900`}>{r.sheets.toLocaleString()}</td>
                      {canRecord && (
                        <td className={`${td} text-right whitespace-nowrap`}>
                          {data.shiftsReady && r.operator !== '(not given)' && (
                            <button onClick={() => setEditing(r)} className="text-xs font-medium text-brand-700 hover:underline">
                              {r.shiftId ? 'Edit hours' : 'Add hours'}
                            </button>
                          )}
                          {r.shiftId != null && (
                            <button
                              onClick={() => { if (confirm(`Remove ${r.operator}'s hours for ${sheetDate(r.date)}?`)) del.mutate(r.shiftId!); }}
                              className="ml-3 text-xs font-medium text-red-600 hover:underline"
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={canRecord ? 10 : 9} className="px-4 py-8 text-center text-slate-500">No shifts or cuts in these dates.</td></tr>
                  )}
                  {rows.length > 0 && (
                    <tr className="bg-slate-50 font-medium text-slate-900">
                      <td className={td} colSpan={5}>Total</td>
                      <td className={`${td} text-right`}>{hhmm(total.hours)}</td>
                      <td className={td}></td>
                      <td className={`${td} text-right`}>{total.jobs.toLocaleString()}</td>
                      <td className={`${td} text-right`}>{total.sheets.toLocaleString()}</td>
                      {canRecord && <td className={td}></td>}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {rows.length > 0 && (
            <div className="grid gap-6 lg:grid-cols-2">
              {[{ title: 'Dept Summary', first: 'Date', list: byDay, label: (k: string) => sheetDate(k) },
                { title: 'Operator Summary', first: 'Operator', list: byOperator, label: (k: string) => k }].map((t) => (
                <div key={t.title} className={card}>
                  <div className="px-4 py-3 text-sm font-bold text-slate-800 border-b border-slate-100">{t.title}</div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className={head}>
                        <tr>
                          <th className={th}>{t.first}</th>
                          <th className={`${th} text-right`}>Jobs</th>
                          <th className={`${th} text-right`}>Hours</th>
                          <th className={`${th} text-right`}>Sheets</th>
                          <th className={`${th} text-right`}>Jobs / hr</th>
                          <th className={`${th} text-right`}>Sheets / hr</th>
                          <th className={`${th} text-right`}>Sheets / job</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {[...t.list, { key: 'Total', ...total }].map((d, i) => (
                          <tr key={d.key} className={i === t.list.length ? 'bg-slate-50 font-medium' : ''}>
                            <td className={`${td} text-slate-900 whitespace-nowrap`}>{i === t.list.length ? 'Total' : t.label(d.key)}</td>
                            <td className={`${td} text-right`}>{d.jobs.toLocaleString()}</td>
                            <td className={`${td} text-right`}>{hhmm(d.hours)}</td>
                            <td className={`${td} text-right`}>{d.sheets.toLocaleString()}</td>
                            <td className={`${td} text-right text-slate-600`}>{perHour(d.jobs, d.hours)}</td>
                            <td className={`${td} text-right text-slate-600`}>{perHour(d.sheets, d.hours)}</td>
                            <td className={`${td} text-right text-slate-600`}>{perJob(d.sheets, d.jobs)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {editing && (
        <ShiftModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['cutting-daily'] }); }}
        />
      )}
    </>
  );
}

/** An operator's hours for one working day. */
function ShiftModal({ initial, onClose, onSaved }: { initial: Partial<DailyRow>; onClose: () => void; onSaved: () => void }) {
  const operators = useOperators();
  const [date, setDate] = useState(initial.date ?? '');
  const [operator, setOperator] = useState(initial.operator ?? '');
  const [start, setStart] = useState(initial.startTime || '08:30');
  const [end, setEnd] = useState(initial.endTime || '20:30');
  const [brk, setBrk] = useState<number | null>(initial.breakMin ?? 30);
  const [shift, setShift] = useState(initial.shift || 'D');

  const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const hours = start && end ? Math.max((((mins(end) - mins(start)) % 1440) + 1440) % 1440 - (brk ?? 0), 0) / 60 : null;

  const mut = useMutation({
    mutationFn: () => api('/api/cutting/shifts', {
      method: 'PUT',
      body: JSON.stringify({ workDate: date, operatorName: operator, startTime: start, endTime: end, breakMin: brk ?? 0, shift }),
    }),
    onSuccess: () => { rememberOperator(operator); onSaved(); },
  });
  const input = 'w-full border px-3 py-2 border-slate-200 rounded-md focus:border-brand-500 focus:ring-brand-500 sm:text-sm';
  const label = 'block text-sm font-medium text-slate-700 mb-1';

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800">{initial.shiftId ? 'Edit shift' : 'Shift entry'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">&times;</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={label}>Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} className={input} />
            </div>
            <div>
              <label className={label}>Operator</label>
              <select value={operator} onChange={e => setOperator(e.target.value)} className={input}>
                <option value="">Choose…</option>
                {(operators.data ?? []).map((n) => <option key={n} value={n}>{n}</option>)}
                {operator && !(operators.data ?? []).includes(operator) && <option value={operator}>{operator}</option>}
              </select>
            </div>
            <div>
              <label className={label}>Start time</label>
              <input type="time" value={start} onChange={e => setStart(e.target.value)} className={input} />
            </div>
            <div>
              <label className={label}>End time</label>
              <input type="time" value={end} onChange={e => setEnd(e.target.value)} className={input} />
            </div>
            <div>
              <label className={label}>Break (minutes)</label>
              <input type="number" min="0" value={brk ?? ''} onChange={e => setBrk(e.target.value ? Number(e.target.value) : null)} className={input} />
            </div>
            <div>
              <label className={label}>Shift</label>
              <select value={shift} onChange={e => setShift(e.target.value)} className={input}>
                <option value="D">D</option>
                <option value="N">N</option>
                <option value="D+N">D+N</option>
              </select>
            </div>
          </div>
          <div className="text-sm text-slate-600">
            Hours: <span className="font-medium text-slate-900">{hhmm(hours)}</span>
            {start && end && mins(end) <= mins(start) && <span className="text-xs text-slate-500"> · ends the next morning</span>}
          </div>
          <div className="text-xs text-slate-500">One entry per operator per day; saving again replaces it. Cuts made after midnight (before 7 am) count for the day the shift started.</div>
          {mut.isError && <div className="text-sm text-red-600">{cuttingError(mut.error)}</div>}
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">Cancel</button>
          <button
            onClick={() => mut.mutate()}
            disabled={!date || !operator || !start || !end || mut.isPending}
            className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700 disabled:opacity-50"
          >
            {mut.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Start / End shift

interface OpenShift { shiftId: number; date: string; operator: string; startTime: string; shift: string }

/** Shifts started and not yet ended (the last two days). */
function useOpenShifts(enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['cutting-open-shifts'],
    queryFn: () => api<{ rows: OpenShift[]; ready: boolean }>('/api/cutting/shifts/open'),
    staleTime: 30_000,
  });
}

const PlayIcon = ({ className = '' }: { className?: string }) => (
  <svg aria-hidden viewBox="0 0 12 12" className={`h-3 w-3 fill-current ${className}`}><path d="M2.5 1.5v9l8-4.5z" /></svg>
);
const StopIcon = ({ className = '' }: { className?: string }) => (
  <svg aria-hidden viewBox="0 0 12 12" className={`h-3 w-3 fill-current ${className}`}><rect x="2" y="2" width="8" height="8" rx="1" /></svg>
);

const pad2 = (n: number) => String(n).padStart(2, '0');
/** The time now as HH:MM (the desk's clock is India time). */
const nowHHMM = () => { const d = new Date(); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
/** The clock, ticking every 15 seconds while a dialog shows it. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(t);
  }, []);
  return now;
}
/** Today's working day, as the API counts it: before 7 am is the day before. */
const workDayNow = () => isoDay(new Date(Date.now() - 7 * 3600_000));
/** When an open shift started: "9:25 am", with its day when it is not today's, e.g. "9:25 am, 30 Oct". */
function openSince(r: OpenShift): string {
  if (r.date === workDayNow()) return timeText(r.startTime);
  return `${timeText(r.startTime)}, ${new Date(`${r.date}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
}
/** 08:30 as 8:30 am. */
function timeText(t: string): string {
  const h = Number(t.slice(0, 2)), m = t.slice(3, 5);
  return Number.isNaN(h) ? t : `${((h + 11) % 12) + 1}:${m} ${h < 12 ? 'am' : 'pm'}`;
}
const SHIFT_TEXT: Record<string, string> = { D: 'Day', N: 'Night', 'D+N': 'Day + Night' };
const fieldCls = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-base text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30';

function Step({ n, children, htmlFor }: { n: number; children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-semibold text-slate-700 mb-2">
      <span className="mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-[11px] font-bold text-brand-700">{n}</span>
      {children}
    </label>
  );
}

function Chip({ on, onClick, children, disabled, title }: { on: boolean; onClick: () => void; children: ReactNode; disabled?: boolean; title?: string }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
      className={`rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
        on ? 'border-brand-600 bg-brand-600 text-white shadow-sm' : 'border-slate-300 bg-white text-slate-700 hover:border-brand-400 hover:bg-brand-50'
      }`}>
      {on && '✓ '}{children}
    </button>
  );
}

/** The modal frame both steps share: a coloured header, a body that scrolls, a footer. */
function ShiftFrame({ tone, kicker, title, onClose, busy, onSubmit, footer, children }: {
  tone: 'start' | 'end'; kicker: string; title: string; onClose: () => void; busy: boolean;
  onSubmit: () => void; footer: ReactNode; children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);
  const head = tone === 'start' ? 'from-emerald-700 to-emerald-600' : 'from-slate-800 to-slate-700';
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-3 sm:p-4 z-50">
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[94vh] flex flex-col overflow-hidden">
        <div className={`px-5 sm:px-6 py-4 bg-gradient-to-br ${head} text-white flex items-start justify-between gap-3`}>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-white/70">{kicker}</div>
            <div className="mt-0.5 text-xl font-bold leading-tight">{title}</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="shrink-0 rounded-full w-9 h-9 flex items-center justify-center text-2xl leading-none text-white/80 hover:bg-white/15 hover:text-white">&times;</button>
        </div>
        <div className="overflow-y-auto overflow-x-hidden px-5 sm:px-6 py-5 space-y-5">{children}</div>
        <div className="px-5 sm:px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">{footer}</div>
      </form>
    </div>
  );
}

/** Super Admin keeps the operator list: add a name, take one off. */
function OperatorsManager() {
  const qc = useQueryClient();
  const operators = useOperators();
  const [name, setName] = useState('');
  const done = () => qc.invalidateQueries({ queryKey: ['cutting-operators'] });
  const add = useMutation({
    mutationFn: (n: string) => api('/api/cutting/operators', { method: 'POST', body: JSON.stringify({ name: n }) }),
    onSuccess: () => { setName(''); done(); },
  });
  const remove = useMutation({
    mutationFn: (n: string) => api(`/api/cutting/operators/${encodeURIComponent(n)}`, { method: 'DELETE' }),
    onSuccess: done,
  });
  const err = add.error ?? remove.error;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Operators · Super Admin</div>
      <div className="flex flex-wrap gap-2">
        {(operators.data ?? []).map((n) => (
          <span key={n} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white pl-3 pr-1 py-1 text-sm text-slate-700">
            {n}
            <button type="button" title={`Remove ${n}`} disabled={remove.isPending}
              onClick={() => { if (confirm(`Remove ${n} from the operators?\n\nTheir past cuts and shifts keep the name.`)) remove.mutate(n); }}
              className="w-6 h-6 rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600">&times;</button>
          </span>
        ))}
        {(operators.data ?? []).length === 0 && <span className="text-sm text-slate-500">No operators yet.</span>}
      </div>
      <div className="flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New operator's name"
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (name.trim()) add.mutate(name.trim()); } }}
          className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        <button type="button" disabled={!name.trim() || add.isPending} onClick={() => add.mutate(name.trim())}
          className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">Add</button>
      </div>
      {err && <div className="text-xs text-red-600">{cuttingError(err)}</div>}
    </div>
  );
}

/**
 * Step one of a shift: who, and day or night.  It starts now: the time and the
 * working day come from the clock (the API's), not the screen.  Someone
 * already on shift can't start again until that shift is ended.
 */
function StartShiftModal({ onClose, onSaved }: { onClose: () => void; onSaved: (name: string, at: string) => void }) {
  const { user } = useAuth();
  const superAdmin = user?.rollId === ROLE.SuperAdmin;
  const qc = useQueryClient();
  const operators = useOperators();
  const open = useOpenShifts();
  const now = useNow();
  const [picked, setPicked] = useState(lastOperator());
  const hour = now.getHours();
  const [shift, setShift] = useState(hour >= 7 && hour < 19 ? 'D' : 'N');
  const [manage, setManage] = useState(false);

  // Each operator's open shift (the newest, if more than one was left open).
  const onShift = new Map<string, OpenShift>();
  for (const r of open.data?.rows ?? []) if (!onShift.has(r.operator)) onShift.set(r.operator, r);
  const names = operators.data ?? [];
  // The name remembered from last time is not picked if they are on shift.
  const operator = picked && !onShift.has(picked) ? picked : '';
  const mut = useMutation({
    mutationFn: () => api<{ startTime?: string }>('/api/cutting/shifts/start', {
      method: 'POST', body: JSON.stringify({ operatorName: operator, shift }),
    }),
    onSuccess: (r) => {
      rememberOperator(operator);
      qc.invalidateQueries({ queryKey: ['cutting-open-shifts'] }); qc.invalidateQueries({ queryKey: ['cutting-daily'] });
      onSaved(operator, timeText(r?.startTime ?? nowHHMM()));
    },
    onError: () => qc.invalidateQueries({ queryKey: ['cutting-open-shifts'] }),
  });
  const blocked = !operator ? 'Pick who is starting' : null;

  return (
    <ShiftFrame tone="start" kicker="Cutting" title="Start shift" onClose={onClose} busy={mut.isPending}
      onSubmit={() => { if (!blocked && !mut.isPending) mut.mutate(); }}
      footer={<>
        <span className="text-xs text-slate-500">{blocked ?? ''}</span>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-200/60">Cancel</button>
          <button type="submit" disabled={!!blocked || mut.isPending}
            className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed">
            <span className="inline-flex items-center gap-1.5"><PlayIcon />{mut.isPending ? 'Saving…' : operator ? `Start ${operator}'s shift` : 'Start shift'}</span>
          </button>
        </div>
      </>}>
      <div>
        <div className="flex items-center justify-between">
          <Step n={1}>Who is starting?</Step>
          {superAdmin && (
            <button type="button" onClick={() => setManage((m) => !m)} className="mb-2 text-xs font-semibold text-brand-700 hover:underline">
              {manage ? 'Done' : 'Manage operators'}
            </button>
          )}
        </div>
        {manage ? <OperatorsManager /> : (
          <div className="flex flex-wrap gap-2">
            {names.map((n) => {
              const on = onShift.get(n);
              return (
                <Chip key={n} on={operator === n} onClick={() => setPicked(n)} disabled={!!on}
                  title={on ? `Already on shift since ${openSince(on)} — end that shift first` : undefined}>
                  {n}{on && <span className="ml-1 text-[10px] font-semibold opacity-80">· on shift since {openSince(on)}</span>}
                </Chip>
              );
            })}
            {names.length === 0 && (
              <span className="text-sm text-slate-500">{operators.isLoading ? 'Loading…' : 'No operators yet — ask Super Admin to add them.'}</span>
            )}
          </div>
        )}
        {!manage && names.some((n) => onShift.has(n)) && (
          <p className="mt-2 text-xs text-slate-500">Someone already on shift can start again only after their shift is ended (End shift).</p>
        )}
      </div>

      <div>
        <Step n={2}>Starts</Step>
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-base text-slate-900">
          <b>Now</b> · {timeText(`${pad2(now.getHours())}:${pad2(now.getMinutes())}`)},{' '}
          {now.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
        </div>
        <p className="mt-1.5 text-xs text-slate-500">The time and date are taken from the clock when you press Start — they can't be typed.</p>
      </div>

      <div>
        <Step n={3}>Shift</Step>
        <div className="flex gap-2">
          {(['D', 'N', 'D+N'] as const).map((s) => (
            <Chip key={s} on={shift === s} onClick={() => setShift(s)}>{SHIFT_TEXT[s]}</Chip>
          ))}
        </div>
      </div>

      {mut.isError && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{cuttingError(mut.error)}</div>}
    </ShiftFrame>
  );
}

/** Step two: whose shift is ending, when, and the break taken. */
function EndShiftModal({ initialShiftId, onClose, onSaved, onStart }: {
  initialShiftId?: number; onClose: () => void; onSaved: (name: string) => void; onStart: () => void;
}) {
  const qc = useQueryClient();
  const open = useOpenShifts();
  const rows = open.data?.rows ?? [];
  const [picked, setPicked] = useState<number | null>(initialShiftId ?? null);
  const sel = rows.find((r) => r.shiftId === picked) ?? (rows.length === 1 ? rows[0] : undefined);
  const [end, setEnd] = useState(nowHHMM());
  const [brk, setBrk] = useState<number | null>(30);

  const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const hours = sel && end ? Math.max((((mins(end) - mins(sel.startTime)) % 1440) + 1440) % 1440 - (brk ?? 0), 0) / 60 : null;
  const overnight = sel && end ? mins(end) <= mins(sel.startTime) : false;

  const mut = useMutation({
    mutationFn: () => api(`/api/cutting/shifts/${sel!.shiftId}/end`, { method: 'POST', body: JSON.stringify({ endTime: end, breakMin: brk ?? 0 }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cutting-open-shifts'] }); qc.invalidateQueries({ queryKey: ['cutting-daily'] }); onSaved(sel!.operator); },
  });
  const blocked = !sel ? 'Pick whose shift is ending' : !end ? 'Enter the end time' : null;

  return (
    <ShiftFrame tone="end" kicker="Cutting" title="End shift" onClose={onClose} busy={mut.isPending}
      onSubmit={() => { if (!blocked && !mut.isPending) mut.mutate(); }}
      footer={<>
        <span className="text-xs text-slate-500">{blocked ?? ''}</span>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-200/60">Cancel</button>
          <button type="submit" disabled={!!blocked || mut.isPending}
            className="rounded-lg bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed">
            <span className="inline-flex items-center gap-1.5"><StopIcon />{mut.isPending ? 'Saving…' : sel ? `End ${sel.operator}'s shift${hours != null ? ` · ${hhmm(hours)} h` : ''}` : 'End shift'}</span>
          </button>
        </div>
      </>}>
      <div>
        <Step n={1}>Whose shift is ending?</Step>
        {open.isLoading ? <p className="text-sm text-slate-500">Loading…</p>
          : open.data && !open.data.ready ? <p className="text-sm text-amber-700">The database update for start / end shifts has not been run yet.</p>
          : rows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">
              Nobody is on shift.{' '}
              <button type="button" onClick={onStart} className="font-semibold text-emerald-700 hover:underline">Start a shift</button>
            </div>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2">
              {rows.map((r) => {
                const on = sel?.shiftId === r.shiftId;
                return (
                  <button key={r.shiftId} type="button" onClick={() => setPicked(r.shiftId)}
                    className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${
                      on ? 'border-brand-600 bg-brand-50 ring-2 ring-brand-500/30' : 'border-slate-300 bg-white hover:border-brand-400'
                    }`}>
                    <div className="font-semibold text-slate-800">{on && '✓ '}{r.operator}</div>
                    <div className="text-xs text-slate-500">
                      {SHIFT_TEXT[r.shift] ?? r.shift} · since {timeText(r.startTime)}
                      {r.date !== workDayNow() && <> · {sheetDate(r.date)} · <b className="text-amber-700">not ended</b></>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Step n={2} htmlFor="shift-end">End time</Step>
          <input id="shift-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} className={fieldCls} />
          <button type="button" onClick={() => setEnd(nowHHMM())} className="mt-1.5 text-xs font-semibold text-brand-700 hover:underline">Now</button>
        </div>
        <div>
          <Step n={3} htmlFor="shift-break">Break (minutes)</Step>
          <input id="shift-break" type="number" min="0" inputMode="numeric" value={brk ?? ''} onFocus={(e) => e.target.select()}
            onChange={(e) => setBrk(e.target.value ? Number(e.target.value) : null)} className={fieldCls} />
          <div className="mt-1.5 flex gap-1.5">
            {[0, 30, 60].map((m) => (
              <button key={m} type="button" onClick={() => setBrk(m)}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${brk === m ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-600 hover:border-brand-400'}`}>
                {m}
              </button>
            ))}
          </div>
        </div>
      </div>

      {sel && sel.date !== workDayNow() && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {sel.operator}'s shift started {openSince(sel)} and was never ended. Enter the time it really ended.
        </div>
      )}
      {sel && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-sm text-slate-600">
            {timeText(sel.startTime)} → {end ? timeText(end) : '—'}{(brk ?? 0) > 0 && <> · less {brk} min break</>}
            {overnight && <div className="text-xs text-slate-500">ends the next morning</div>}
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-slate-800">{hhmm(hours)}</div>
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">hours</div>
          </div>
        </div>
      )}

      {mut.isError && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{cuttingError(mut.error)}</div>}
    </ShiftFrame>
  );
}

/** An API error as words an operator can act on. */
function cuttingError(e: unknown): string {
  const m = (e as Error)?.message ?? '';
  if (m === 'forbidden') return 'You can look at the cutting queue but not record on it.';
  if (m === 'migration_needed') return 'The database update for cutting shifts has not been run yet.';
  if (m === 'super_admin_only') return 'Only Super Admin can add or remove operators.';
  if (m === 'shift_already_ended') return "That operator's shift for the day has already ended — change it from the Daily Sheet.";
  if (m.startsWith('already_on_shift:')) {
    // already_on_shift:<yyyy-mm-dd>:<HH:MM>
    const rest = m.slice('already_on_shift:'.length);
    const day = rest.slice(0, 10), start = rest.slice(11);
    return `Already on shift since ${openSince({ shiftId: 0, operator: '', shift: '', date: day, startTime: start })} — end that shift first.`;
  }
  if (m === 'shift_not_open') return 'That shift has already ended — refresh.';
  if (m === 'job_not_found') return 'No job card with that number.';
  if (m === 'job_closed') return 'That job is closed or cancelled.';
  if (m === 'board_not_on_job') return 'That board is not issued to this job.';
  if (m === 'validation_failed') return 'Check the numbers: sheets must be a whole number above 0.';
  return `Could not save: ${m || 'unknown error'}`;
}
